// components/DataTable.tsx
import React from 'react';
import { Planos, mascaraCPF, mascaraTelefone } from '../utils/dataProcessing';

interface DataTableProps {
  data: any[];
  selectedRows: string[];
  selectedDependentes: { [key: string]: string[] };
  expandedRows: string[];
  headerCheckboxRef: React.RefObject<HTMLInputElement>;
  onToggleSelect: (titularId: string) => void;
  onToggleSelectDependente: (titularId: string, dependenteId: string) => void;
  onToggleExpand: (titularId: string) => void;
  onToggleAll: () => void;
  onToggleAllDependentes: (titularId: string) => void;
  onDeleteSelected: () => void;
  onDeleteSelectedDependentes: (titularId: string) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  data,
  selectedRows,
  selectedDependentes,
  expandedRows,
  headerCheckboxRef,
  onToggleSelect,
  onToggleSelectDependente,
  onToggleExpand,
  onToggleAll,
  onToggleAllDependentes,
  onDeleteSelected,
  onDeleteSelectedDependentes,
}) => {
  // Colunas visíveis (excluindo campos internos)
  const visibleKeys = data?.[0]
    ? Object.keys(data[0]).filter(
        key =>
          key !== 'id' &&
          key !== 'dependentes' &&
          key !== 'telefone1' &&
          key !== 'telefone2' &&
          key !== 'endereco' &&
          key !== 'OrgaoExp' &&
          key !== 'matriculaEmpresa',
      )
    : [];

  // Renderizar cabeçalho da tabela
  const renderTableHeader = () => (
    <thead className="bg-blue-100 sticky top-0 z-10">
      <tr>
        <th className="flex px-2 text-center items-center">
          <input
            type="checkbox"
            ref={headerCheckboxRef}
            checked={selectedRows.length === data.length && data.length > 0}
            onChange={onToggleAll}
            style={{ marginRight: '0.5rem' }}
          />
          Selecionar Todos
          {selectedRows.length > 0 && (
            <button
              type="button"
              onClick={onDeleteSelected}
              className="ml-2 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Excluir
            </button>
          )}
        </th>
        <th className="px-2 text-center">Dependentes</th>
        {visibleKeys.map(key => (
          <th key={key} className="px-5 whitespace-nowrap">
            {getColumnLabel(key)}
          </th>
        ))}
      </tr>
    </thead>
  );

  // Renderizar linha do titular
  const renderTitularRow = (row: any) => {
    const isExpanded = expandedRows.includes(row.id);

    return (
      <tr key={row.id}>
        <td className="border-t py-3 p-3 text-center">
          <input
            type="checkbox"
            checked={selectedRows.includes(row.id)}
            onChange={() => onToggleSelect(row.id)}
          />
        </td>

        <td className="border-t py-3 p-3 text-center">
          {row.dependentes?.length > 0 ? (
            <button
              onClick={() => onToggleExpand(row.id)}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200 transition"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              {row.dependentes.length}
              {isExpanded ? ' ▼' : ' ▶'}
            </button>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {Object.entries(row)
          .filter(([key]) => visibleKeys.includes(key))
          .map(([key, val], j) => (
            <td
              key={j}
              className="border-t py-3 p-3 whitespace-nowrap text-center"
            >
              {formatCellValue(key, val)}
            </td>
          ))}
      </tr>
    );
  };

  // Renderizar linha expandida com dependentes
  const renderDependentesRow = (row: any) => {
    const isExpanded = expandedRows.includes(row.id);

    if (!isExpanded || !row.dependentes || row.dependentes.length === 0) {
      return null;
    }

    return (
      <tr key={`${row.id}-dependentes`}>
        <td colSpan={visibleKeys.length + 2} className="bg-blue-50">
          <div className="p-4">
            <h4 className="font-semibold text-blue-800 mb-2">
              Dependentes de {row.nome}:
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-700 border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input
                        type="checkbox"
                        id={`header-dep-${row.id}`}
                        checked={
                          selectedDependentes[row.id]?.length ===
                            row.dependentes.length && row.dependentes.length > 0
                        }
                        onChange={() => onToggleAllDependentes(row.id)}
                        className="mr-2"
                      />
                      Selecionar
                      {selectedDependentes[row.id]?.length > 0 && (
                        <button
                          type="button"
                          onClick={() => onDeleteSelectedDependentes(row.id)}
                          className="ml-2 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs"
                        >
                          Excluir
                        </button>
                      )}
                    </th>
                    {Object.keys(row.dependentes[0]).map(depKey => (
                      <th key={depKey} className="px-4 py-2 text-left">
                        {getColumnLabel(depKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {row.dependentes.map((dep: any) => (
                    <tr key={dep.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={
                            selectedDependentes[row.id] &&
                            selectedDependentes[row.id].includes(dep.id)
                          }
                          onChange={() =>
                            onToggleSelectDependente(row.id, dep.id)
                          }
                        />
                      </td>
                      {Object.entries(dep).map(([depKey, dVal], k) => (
                        <td key={k} className="px-4 py-2">
                          {formatCellValue(depKey, dVal)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Função para formatar valores das células
  const formatCellValue = (key: string, value: any): string => {
    if (key === 'plano') {
      return (
        Planos.find(p => String(p.id) === String(value))?.nome || String(value)
      );
    }
    if (key === 'cpf') {
      return mascaraCPF(String(value));
    }
    if (key === 'celular' || key === 'telefone1' || key === 'telefone2') {
      return mascaraTelefone(String(value));
    }
    return String(value || '');
  };

  // Função para obter rótulos das colunas
  const getColumnLabel = (key: string): string => {
    const labels: { [key: string]: string } = {
      nomeMae: 'Nome da Mãe',
      dataNascimento: 'Data de Nascimento',
      email: 'E-mail',
      estCivil: 'Estado Civil',
      cpf: 'CPF',
      nome: 'Nome',
      plano: 'Plano',
      cns: 'CNS',
      sexo: 'Sexo',
      celular: 'Celular',
      parentesco: 'Parentesco',
    };
    return labels[key] || key;
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Nenhum dado para exibir
      </div>
    );
  }

  return (
    <div className="mt-4 overflow-auto border w-full h-[60vh] rounded-lg">
      <table className="text-sm text-center text-blue-800 border-collapse">
        {renderTableHeader()}
        <tbody>
          {data.map(row => (
            <React.Fragment key={row.id}>
              {renderTitularRow(row)}
              {renderDependentesRow(row)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
